import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logo from "../../../assets/images/logo.jpeg";
import { GlobalContext } from "../../../context";
import LanguageSwitcher from "../../LanguageSwitcher";

/**
 * ArdrendHeader
 * - On homepage (alwaysScrolled=false): starts transparent, becomes white on scroll
 * - On all other pages (alwaysScrolled=true): always renders the white/scrolled state
 */
const ArdrendHeader = ({ alwaysScrolled = false }) => {
  const { t } = useTranslation();
  const { authState: { loggedIn } } = useContext(GlobalContext);
  const [scrolled, setScrolled] = useState(alwaysScrolled);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (alwaysScrolled) return; // skip scroll listener on inner pages
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [alwaysScrolled]);

  const isScrolled = alwaysScrolled || scrolled;

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? "bg-white shadow-sm border-b border-gray-100 py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-[1560px] mx-auto px-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <img
            src={logo}
            alt="E1X Academy Logo"
            className={`object-contain transition-all duration-300 ${isScrolled ? "h-10" : "h-10 md:h-12"}`}
          />
        </Link>

        {/* Desktop: Menu Pill */}
        <div className="hidden lg:flex bg-white rounded-full px-6 xl:px-8 py-3.5 items-center gap-6 xl:gap-8 shadow-sm border border-gray-100">
          <Link to="/" className="text-[12px] xl:text-[13px] font-bold text-[#0b0f19] hover:text-primary uppercase tracking-widest transition-colors">
            {t("nav.home")}
          </Link>
          <Link to="/courses" className="text-[12px] xl:text-[13px] font-bold text-[#0b0f19] hover:text-primary uppercase tracking-widest transition-colors">
            {t("nav.courses")}
          </Link>
          <Link to="/scholarships" className="text-[12px] xl:text-[13px] font-bold text-[#0b0f19] hover:text-primary uppercase tracking-widest transition-colors">
            {t("nav.scholarships")}
          </Link>

          {/* Company Dropdown */}
          <div className="relative group">
            <button className="text-[12px] xl:text-[13px] font-bold text-[#0b0f19] hover:text-primary uppercase tracking-widest transition-colors flex items-center gap-1.5 focus:outline-none">
              {t("nav.company")}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-3.5 h-3.5 transition-transform group-hover:rotate-180 text-gray-400 group-hover:text-primary">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Hover Dropdown */}
            <div className="absolute top-full left-1/2 -translate-x-1/2 pt-6 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50 pointer-events-none group-hover:pointer-events-auto">
              <div className="bg-white border border-gray-100 shadow-2xl rounded-2xl w-[580px] p-8 flex gap-8">
                <div className="flex-1">
                  <h4 className="font-bold text-[10px] uppercase tracking-wider text-gray-400 mb-4">{t("nav.aboutSupport")}</h4>
                  <div className="flex flex-col gap-3">
                    <Link to="/about" className="text-[13px] font-bold text-[#0b0f19] hover:text-primary transition-colors block">{t("nav.aboutUs")}</Link>
                    <Link to="/contact" className="text-[13px] font-bold text-[#0b0f19] hover:text-primary transition-colors block">{t("nav.contactUs")}</Link>
                    <Link to="/alumni-stories" className="text-[13px] font-bold text-[#0b0f19] hover:text-primary transition-colors block">{t("nav.alumniStories")}</Link>
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-[10px] uppercase tracking-wider text-gray-400 mb-4">{t("nav.e1xHub")}</h4>
                  <div className="flex flex-col gap-3">
                    <Link to="/blog" className="text-[13px] font-bold text-[#0b0f19] hover:text-primary transition-colors block">{t("nav.blogInsights")}</Link>
                    <Link to="/webinars-events" className="text-[13px] font-bold text-[#0b0f19] hover:text-primary transition-colors block">{t("nav.eventsMeetups")}</Link>
                    <Link to="/faq" className="text-[13px] font-bold text-[#0b0f19] hover:text-primary transition-colors block">{t("nav.faq")}</Link>
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-[10px] uppercase tracking-wider text-gray-400 mb-4">{t("nav.partnership")}</h4>
                  <div className="flex flex-col gap-3">
                    <Link to="/sponsor" className="text-[13px] font-bold text-[#0b0f19] hover:text-primary transition-colors block">{t("nav.sponsors")}</Link>
                    <Link to="/talents" className="text-[13px] font-bold text-[#0b0f19] hover:text-primary transition-colors block">{t("nav.talentsPortal")}</Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Link to="/consultancy" className="text-[12px] xl:text-[13px] font-bold text-[#0b0f19] hover:text-primary uppercase tracking-widest transition-colors">
            {t("nav.consultancy")}
          </Link>

          <Link to={loggedIn ? "/dashboard" : "/login"} className="text-[12px] xl:text-[13px] font-bold text-[#0b0f19] hover:text-primary uppercase tracking-widest transition-colors">
            {t("nav.lms")}
          </Link>
        </div>

        {/* Desktop: Language + Get Started CTA */}
        <div className="hidden lg:flex items-center gap-3 xl:gap-4">
          <LanguageSwitcher className={isScrolled ? "" : "bg-white/10 border-white/20"} />
          <button
            onClick={() => navigate("/login")}
            className="bg-primary hover:bg-[#b0451e] transition-colors rounded-full pl-6 pr-2 py-2 flex items-center gap-3 group shadow-md cursor-pointer"
          >
            <span className="text-[12px] xl:text-[13px] font-bold text-white uppercase tracking-wider">
              {t("nav.getStarted")}
            </span>
            <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-primary group-hover:bg-[#0b0f19] group-hover:text-white transition-colors">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
              </svg>
            </div>
          </button>
        </div>

        {/* Mobile: Hamburger - modern icon */}
        <button
          className={`lg:hidden p-2 rounded-xl transition-colors ${
            isScrolled ? "text-gray-800 hover:bg-gray-100" : "text-white hover:bg-white/10"
          }`}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={t("nav.toggleMenu")}
        >
          {mobileOpen ? (
            /* Modern X close icon */
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
              <circle cx="12" cy="12" r="10" className="opacity-20" fill="currentColor" stroke="none"/>
              <path d="M8 8l8 8M16 8l-8 8" />
            </svg>
          ) : (
            /* Modern staggered menu icon */
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-6 h-6">
              <line x1="3" y1="7" x2="21" y2="7" />
              <line x1="6" y1="12" x2="21" y2="12" />
              <line x1="9" y1="17" x2="21" y2="17" />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg absolute w-full">
          <div className="px-6 py-6 flex flex-col gap-5">
            <Link to="/" onClick={() => setMobileOpen(false)} className="text-sm font-bold uppercase tracking-widest text-[#0b0f19] hover:text-primary transition-colors">{t("nav.home")}</Link>
            <Link to="/courses" onClick={() => setMobileOpen(false)} className="text-sm font-bold uppercase tracking-widest text-[#0b0f19] hover:text-primary transition-colors">{t("nav.courses")}</Link>
            <Link to="/scholarships" onClick={() => setMobileOpen(false)} className="text-sm font-bold uppercase tracking-widest text-[#0b0f19] hover:text-primary transition-colors">{t("nav.scholarships")}</Link>

            <div className="border-t border-gray-100 pt-4">
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-4">{t("nav.company")}</p>
              <div className="grid grid-cols-2 gap-3 pl-1">
                <Link to="/about" onClick={() => setMobileOpen(false)} className="text-sm font-semibold text-gray-600 hover:text-primary">{t("nav.aboutUs")}</Link>
                <Link to="/contact" onClick={() => setMobileOpen(false)} className="text-sm font-semibold text-gray-600 hover:text-primary">{t("nav.contactUs")}</Link>
                <Link to="/alumni-stories" onClick={() => setMobileOpen(false)} className="text-sm font-semibold text-gray-600 hover:text-primary">{t("nav.alumni")}</Link>
                <Link to="/blog" onClick={() => setMobileOpen(false)} className="text-sm font-semibold text-gray-600 hover:text-primary">{t("nav.blog")}</Link>
                <Link to="/webinars-events" onClick={() => setMobileOpen(false)} className="text-sm font-semibold text-gray-600 hover:text-primary">{t("nav.events")}</Link>
                <Link to="/faq" onClick={() => setMobileOpen(false)} className="text-sm font-semibold text-gray-600 hover:text-primary">{t("nav.faq")}</Link>
              </div>
            </div>

            <Link to="/consultancy" onClick={() => setMobileOpen(false)} className="text-sm font-bold uppercase tracking-widest text-[#0b0f19] hover:text-primary transition-colors border-t border-gray-100 pt-4">{t("nav.consultancy")}</Link>
            <Link to={loggedIn ? "/dashboard" : "/login"} onClick={() => setMobileOpen(false)} className="text-sm font-bold uppercase tracking-widest text-[#0b0f19] hover:text-primary transition-colors">{t("nav.lms")}</Link>

            <div className="border-t border-gray-100 pt-4 flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">{t("language.label")}</span>
              <LanguageSwitcher />
            </div>

            <button
              onClick={() => { navigate("/login"); setMobileOpen(false); }}
              className="w-full bg-primary hover:bg-[#b0451e] text-white font-bold text-sm py-4 rounded-full transition-colors mt-2"
            >
              {t("nav.getStarted")}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default ArdrendHeader;
